import React, { useState } from 'react';
import PropTypes from 'prop-types';
import PackageSelection from '../../shop/StoreGallery/PackageSelection';
import PackageBuilder from '../../shop/StoreGallery/PackageBuilder';

/**
 * Admin preview of a Shop::Product exactly as a customer sees it.
 *
 * It mounts the REAL storefront components (PackageSelection / PackageBuilder)
 * against the REAL serializer output (Shop::ProductSerializer), so the preview
 * cannot drift from the store. Nothing here re-implements a card or a builder --
 * if it renders differently in the shop, that is a bug in the shop, not here.
 *
 * The only fictions are the photo (a bundled placeholder portrait, since a
 * product has no student) and the callbacks, which are inert.
 */
const noop = () => {};

// Flex column that may shrink (minHeight: 0), so `stage` below can be handed the
// height left over in the sticky preview pane rather than capping itself.
const wrap = {
  border: '1px solid #ddd',
  borderRadius: 8,
  overflow: 'hidden',
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  flex: 1,
};
const bar = {
  display: 'flex',
  alignItems: 'center',
  gap: 0,
  padding: '6px 12px 0',
  background: '#f5f5f5',
  borderBottom: '1px solid #ddd',
  flexWrap: 'wrap',
};
// Underlined tabs, not filled buttons. A solid blue button reads as the thing to
// press -- a call to action -- and these only switch which view of the preview you
// are looking at. Nothing here is an action at all: every callback is inert.
//
// `border` and `background` are set explicitly because a bare <button> keeps the
// user agent's own border and grey `buttonface` otherwise (this file is plain CSS,
// with no framework reset behind it).
const tab = (on) => ({
  padding: '4px 1px',
  marginRight: 16,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: on ? 600 : 400,
  border: 'none',
  borderBottom: `2px solid ${on ? '#1976d2' : 'transparent'}`,
  borderRadius: 0,
  background: 'transparent',
  color: on ? '#1976d2' : '#555',
});
const note = { fontSize: 12, color: '#777', marginLeft: 'auto' };
// The storefront's checkout bar is `position: fixed` (PackageSelection stickyBar),
// so picking a package in the PREVIEW pinned a real-looking "Add to cart" across the
// bottom of the ADMIN window, over the wizard, wired to an inert callback.
//
// A `transform` makes this element the containing block for fixed descendants, so
// the bar pins to the bottom of the preview instead of the viewport. Done here
// rather than in the storefront component: the preview mounts the REAL components
// on purpose, and a customer's bar SHOULD be fixed. Note this frame must not be the
// scrolling box -- a fixed child of a scroll container it also contains would ride
// the content instead of staying put.
const frame = {
  position: 'relative', transform: 'translateZ(0)', flex: 1, minHeight: 0, display: 'flex',
};
// THE scroller for the preview, and the only one: the pane around it is a flex
// column that does not scroll, so this takes the height that is left. It used to cap
// itself at 620px inside a pane that ALSO scrolled, which put two bars side by side
// -- and the storefront page it holds sets minHeight: 100vh, so the inner one was
// guaranteed to appear and to be long.
const stage = {
  padding: 12, overflowY: 'auto', flex: 1, minHeight: 0, width: '100%',
};
const empty = {
  padding: 24, color: '#777', fontSize: 14, textAlign: 'center',
};

// Where the store sells a product that is NOT a photo package. Those never appear
// in Choose Your Photo Package (StoreController#current_season_products_for keeps
// to Shop::Product::PHOTO_TYPES), so drawing them as package cards would preview a
// page no customer sees.
const ELSEWHERE = {
  printess: 'Personalised items are offered after a photo package is added (the '
    + '"personalised" step of the post-add chain) — never as a photo package.',
  addon: 'Add-ons are offered on a photo package already in the cart — never as a '
    + 'photo package of their own.',
};

const ShopProductPreview = ({
  product, templates, samplePose, backgroundUrl, studentName, photoPackage, productType,
}) => {
  const variants = (product && product.variants) || [];
  // Only an option with sheets can be configured. The Builder tab used to mount the
  // configurator on variants[0] whatever it was; on product 67 that is "Option
  // 2729" -- priced, offering styles, but with no legacy option behind it, so no
  // sheets -- and PackageBuilder threw on units[0].key, taking the whole preview
  // down. Not narrowed to the store's own multi-or-styled routing rule: a
  // single-sheet unstyled option is one-click in the store, but it still HAS a sheet
  // and an admin looking at the Builder tab is entitled to see it.
  const buildable = variants.filter((v) => (v.slots || []).length > 0);
  const [view, setView] = useState('cards');
  const [variantId, setVariantId] = useState(buildable[0] ? buildable[0].id : null);
  const [oneClick, setOneClick] = useState(null);
  const variant = variants.find((v) => v.id === variantId) || buildable[0] || null;

  // Mirrors StoreGallery#selectVariant exactly: a multi-print or styled package
  // opens the builder, anything else goes straight into the cart. Without this the
  // card's own button was inert -- the admin presses the one control the preview
  // draws most prominently and nothing at all happens.
  const select = (chosen) => {
    const styled = (chosen.template_ids || []).length > 0;
    if (chosen.multi || styled) {
      setVariantId(chosen.id);
      setOneClick(null);
      setView('builder');
      return;
    }
    // There is no cart to add to here, so say what the store would have done
    // rather than swallowing the click.
    setOneClick(chosen.name);
  };

  if (!product || !variants.length) {
    return (
      <div style={wrap}>
        <div style={empty}>
          Nothing to preview yet — this product has no priced package, so the
          store would not show it to anyone.
        </div>
      </div>
    );
  }

  if (!photoPackage) {
    return (
      <div style={wrap}>
        <div style={{ ...empty, textAlign: 'left' }}>
          <p style={{ marginTop: 0 }}>
            {ELSEWHERE[productType] || 'This product is not sold as a photo package.'}
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {variants.map((v) => (
              <li key={v.id}>{`${v.name} — $${Number(v.price || 0).toFixed(2)}`}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  // The builder is scoped to the styles THIS product offers (same rule the shop uses).
  const offered = templates.filter((t) => (product.template_ids || []).includes(t.id));

  return (
    <div style={wrap}>
      {/* Announced as tabs, since that is now what they look like. */}
      <div style={bar} role="tablist" aria-label="Preview view">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'cards'}
          style={tab(view === 'cards')}
          onClick={() => setView('cards')}
        >
          Package cards
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'builder'}
          style={tab(view === 'builder')}
          onClick={() => setView('builder')}
        >
          Builder
        </button>
        {view === 'builder' && buildable.length > 1 && (
          <select
            value={variant ? variant.id : ''}
            onChange={(e) => setVariantId(Number(e.target.value))}
            style={{ fontSize: 13, padding: '3px 6px' }}
          >
            {buildable.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        )}
        <span style={note}>
          {oneClick
            ? `In the store, “${oneClick}” goes straight into the cart`
            : 'Preview · sample photo'}
        </span>
      </div>

      <div style={frame}>
        <div style={stage}>
          {view === 'cards' && (
            <PackageSelection
              pose={samplePose}
              products={[product]}
              backgroundUrl={backgroundUrl}
              templates={templates}
              studentName={studentName}
              adding={false}
              onBack={noop}
              onSelect={select}
            />
          )}
          {view === 'builder' && !variant && (
            <div style={empty}>
              {'No package opens the builder. The store takes the customer straight '
                + 'to the cart for a single-sheet option that offers no photo style — '
                + 'and an option with no prints linked to it has nothing to set up.'}
            </div>
          )}
          {view === 'builder' && variant && (
            <PackageBuilder
              variant={variant}
              backgrounds={product.backgrounds || []}
              defaultBackgroundId={product.default_background_id}
              poses={[samplePose]}
              backgroundUrl={backgroundUrl}
              templates={offered}
              defaultTemplateId={(product.template_ids || [])[0] || null}
              templateRequired={!!product.template_required}
              studentName={studentName}
              adding={false}
              onConfirm={noop}
              onBack={noop}
            />
          )}
        </div>
      </div>
    </div>
  );
};

ShopProductPreview.propTypes = {
  // Shop::ProductSerializer#as_json — the same payload /shop/products.json serves.
  product: PropTypes.shape({
    variants: PropTypes.arrayOf(PropTypes.object),
    backgrounds: PropTypes.arrayOf(PropTypes.object),
    template_ids: PropTypes.arrayOf(PropTypes.number),
    template_required: PropTypes.bool,
    default_background_id: PropTypes.number,
  }),
  // Shop::TemplateSerializer.for_season output.
  templates: PropTypes.arrayOf(PropTypes.object),
  // A stand-in pose: the bundled placeholder portrait, shaped like a real one.
  samplePose: PropTypes.shape({
    id: PropTypes.number,
    watermark_url: PropTypes.string,
  }).isRequired,
  backgroundUrl: PropTypes.string,
  studentName: PropTypes.string,
  // false for a Printess set or add-on: the store never draws those as package cards.
  photoPackage: PropTypes.bool,
  productType: PropTypes.string,
};

ShopProductPreview.defaultProps = {
  product: null,
  templates: [],
  backgroundUrl: null,
  studentName: 'Sample Student',
  photoPackage: true,
  productType: null,
};

export default ShopProductPreview;
